import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ChartBar, Users, Package, Warning, CurrencyCircleDollar,
  ShieldCheck, ShieldWarning, Check, X, Trash, Storefront, MagnifyingGlass, Gavel,
  Lightning, Gear, ChatCircleDots, Sparkle, Article, TrendUp, Fire, ClockCounterClockwise, MapPin
} from "@phosphor-icons/react";

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: ChartBar, group: "Genel" },
  { id: "moderation", label: "AI Onayı", icon: ShieldCheck, group: "Operasyon" },
  { id: "listings", label: "İlanlar", icon: Package, group: "Operasyon" },
  { id: "auctions", label: "Açık Artırmalar", icon: Gavel, group: "Operasyon" },
  { id: "users", label: "Kullanıcılar", icon: Users, group: "Topluluk" },
  { id: "complaints", label: "Şikayetler", icon: Warning, group: "Topluluk" },
  { id: "risky_messages", label: "Riskli Mesajlar", icon: ChatCircleDots, group: "Topluluk" },
  { id: "growth", label: "Growth", icon: TrendUp, group: "Büyüme" },
  { id: "doping", label: "Doping", icon: Lightning, group: "Büyüme" },
  { id: "blog", label: "Rehberler", icon: Article, group: "İçerik" },
  { id: "finance", label: "Finans", icon: CurrencyCircleDollar, group: "Sistem" },
  { id: "ai", label: "AI Kullanım", icon: Sparkle, group: "Sistem" },
  { id: "settings", label: "Ayarlar", icon: Gear, group: "Sistem" },
];

const TAB_GROUPS = ["Genel", "Operasyon", "Topluluk", "Büyüme", "İçerik", "Sistem"];

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/giris"); return; }
    if (user.role !== "admin") { toast.error("Bu sayfaya sadece admin erişebilir"); nav("/"); }
  }, [loading, user, nav]);

  if (!user || user.role !== "admin") return null;

  const currentTab = TABS.find((t) => t.id === tab);

  const renderNav = (onSelect) => (
    <div className="space-y-5">
      {TAB_GROUPS.map((g) => (
        <div key={g}>
          <p className="text-[10px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-2 px-3">{g}</p>
          <div className="flex flex-col gap-0.5">
            {TABS.filter((t) => t.group === g).map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`admin-tab-${t.id}`}
                  onClick={() => { setTab(t.id); onSelect?.(); }}
                  className={`relative flex items-center gap-2.5 text-left px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                    active ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`}
                >
                  {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] bg-white rounded-r-full" />}
                  <t.icon size={15} weight="regular" /> {t.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50/40" data-testid="admin-layout">
      <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6 px-4 md:px-6 py-6">
        {/* Sidebar */}
        <aside className="lg:col-span-3 xl:col-span-2" data-testid="admin-sidebar">
          <div className="hidden lg:block sticky top-6 rounded-2xl bg-white premium-border p-5">
            <div className="mb-6 pb-5 border-b divider">
              <p className="text-[10px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-1.5">Operasyon</p>
              <p className="font-display text-lg font-medium tracking-tight text-slate-900">Merkezi</p>
            </div>
            {renderNav()}
          </div>

          {/* Mobile toggle */}
          <div className="lg:hidden flex items-center justify-between mb-4">
            <button
              onClick={() => setMobileNavOpen(true)}
              data-testid="admin-mobile-nav-open"
              className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2.5 rounded-full font-medium text-sm"
            >
              <ChartBar size={14} weight="bold" /> {currentTab?.label || "Menü"}
            </button>
          </div>

          {/* Mobile drawer */}
          {mobileNavOpen && (
            <div className="fixed inset-0 z-50 lg:hidden" data-testid="admin-mobile-drawer">
              <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setMobileNavOpen(false)} />
              <div className="absolute left-0 top-0 bottom-0 w-[85%] max-w-xs bg-white overflow-y-auto rounded-r-2xl">
                <div className="flex items-center justify-between px-5 py-4 border-b divider">
                  <p className="font-display text-lg font-medium tracking-tight text-slate-900">Operasyon Merkezi</p>
                  <button onClick={() => setMobileNavOpen(false)} data-testid="admin-mobile-drawer-close" className="p-2 rounded-full hover:bg-slate-100">
                    <X size={14} weight="bold" />
                  </button>
                </div>
                <div className="p-5">{renderNav(() => setMobileNavOpen(false))}</div>
              </div>
            </div>
          )}
        </aside>

        {/* Main content */}
        <div className="lg:col-span-9 xl:col-span-10 min-w-0" data-testid="admin-content">
          {/* Top bar */}
          <div className="rounded-2xl bg-white premium-border p-5 mb-5 flex items-center gap-4" data-testid="admin-topbar">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-1">Yönetim Paneli</p>
              <h1 className="font-display text-xl md:text-2xl font-medium tracking-tight text-slate-900 flex items-center gap-2 truncate">
                {currentTab?.icon && <currentTab.icon size={18} weight="regular" className="text-slate-600 shrink-0" />}
                {currentTab?.label}
              </h1>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="hidden md:flex items-center gap-2.5 px-3 py-2 rounded-full bg-slate-50 premium-border">
                <span className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-[10px] font-medium">
                  {user.name?.[0] || "A"}
                </span>
                <span className="text-xs font-medium text-slate-800 max-w-[140px] truncate">{user.name}</span>
              </div>
              <button
                onClick={logout}
                data-testid="admin-logout"
                className="text-xs font-medium text-slate-500 hover:text-[#B54037] px-3 py-2 rounded-full hover:bg-red-50 transition-colors"
                title="Çıkış"
              >
                Çıkış
              </button>
            </div>
          </div>

          {/* Tab content */}
          <div className="rounded-2xl bg-white premium-border p-5 md:p-7 min-h-[400px]">
            {tab === "dashboard" && <SummaryTab />}
            {tab === "growth" && <GrowthTab />}
            {tab === "moderation" && <ModerationTab />}
            {tab === "users" && <UsersTab />}
            {tab === "listings" && <ListingsTab />}
            {tab === "auctions" && <AuctionsTab />}
            {tab === "complaints" && <ComplaintsTab />}
            {tab === "risky_messages" && <RiskyMessagesTab />}
            {tab === "doping" && <DopingTab />}
            {tab === "finance" && <FinanceTab />}
            {tab === "ai" && <AIUsageTab />}
            {tab === "blog" && <BlogTab />}
            {tab === "settings" && <SettingsTab />}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ TAB: SUMMARY (Faz 5E Ops Dashboard) ============
function SummaryTab() {
  const [s, setS] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/admin/summary")
      .then(({ data }) => { setS(data); setErr(null); })
      .catch((e) => setErr(e.response?.data?.detail || "Veriler yüklenemedi. Lütfen tekrar deneyin."));
  }, []);

  if (err) return (
    <div className="text-center py-16" data-testid="admin-dashboard-error">
      <p className="text-sm text-[#B54037] mb-4">{err}</p>
      <button onClick={() => window.location.reload()} className="bg-slate-900 text-white px-5 py-2.5 rounded-full font-medium text-sm">Tekrar dene</button>
    </div>
  );
  if (!s) return <DashboardSkeleton />;

  const pendingAI = s.listings.pending || 0;
  const openComplaints = s.complaints.open || 0;
  const rejected = s.listings.rejected || 0;
  const bannedUsers = s.users.banned || 0;
  const urgencyItems = [
    { count: pendingAI, label: "AI moderasyon bekleyen ilan", tab: "moderation", key: "pending-ai", tone: pendingAI > 20 ? "red" : pendingAI > 5 ? "yellow" : "green" },
    { count: openComplaints, label: "Açık şikayet", tab: "complaints", key: "complaints", tone: openComplaints > 10 ? "red" : openComplaints > 0 ? "yellow" : "green" },
    { count: rejected, label: "Reddedilen ilan", tab: "moderation", key: "rejected", tone: "blue" },
    { count: bannedUsers, label: "Banlı kullanıcı", tab: "users", key: "banned", tone: bannedUsers > 0 ? "yellow" : "green" },
  ];
  const hasUrgent = urgencyItems.some((u) => u.tone === "red");

  const cards = [
    { label: "Toplam Kullanıcı", value: s.users.total, sub: `${s.users.verified} doğrulanmış`, testid: "stat-users", tone: "blue" },
    { label: "Mağaza Sayısı", value: s.users.stores, sub: `${bannedUsers} banlı`, testid: "stat-stores", tone: "slate" },
    { label: "Aktif İlan", value: s.listings.active, sub: `${s.listings.total} toplam ilan`, testid: "stat-active", tone: "green" },
    { label: "Bekleyen Onay", value: pendingAI, sub: `${rejected} reddedilmiş`, testid: "stat-pending", tone: pendingAI > 5 ? "yellow" : "slate" },
    { label: "Açık Şikayet", value: openComplaints, sub: `${s.complaints.total} toplam`, testid: "stat-complaints", tone: openComplaints > 0 ? "yellow" : "green" },
    { label: "Platform Kazancı", value: `₺${Number(s.finance.commission_earned).toLocaleString("tr-TR")}`, sub: `%${(s.finance.commission_rate * 100).toFixed(1)} komisyon`, testid: "stat-revenue", tone: "blue" },
    { label: "Ödemeli Sipariş", value: s.finance.orders, sub: `₺${Number(s.finance.total_volume).toLocaleString("tr-TR")} hacim`, testid: "stat-orders", tone: "slate" },
    { label: "Toplam Teklif", value: s.finance.total_bids, sub: "canlı & kapanmış toplam", testid: "stat-bids", tone: "slate" },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Urgency panel */}
      <div className={`rounded-2xl p-5 md:p-6 ${hasUrgent ? "bg-red-50/60 border border-red-100" : "bg-slate-50 premium-border"}`} data-testid="admin-urgency-panel">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {hasUrgent ? (
              <ShieldWarning size={16} weight="fill" className="text-[#B54037]" />
            ) : (
              <ShieldCheck size={16} weight="fill" className="text-emerald-700" />
            )}
            <p className="text-[10px] uppercase tracking-[0.28em] font-medium text-slate-500">
              {hasUrgent ? "Acil müdahale gerekenler" : "Sistem sağlıklı"}
            </p>
          </div>
          <p className="text-xs text-slate-400">Son güncelleme: {new Date().toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {urgencyItems.map((u) => (
            <div key={u.key} className="flex items-center gap-3 bg-white rounded-xl premium-border p-3.5" data-testid={`urgency-${u.key}`}>
              <ToneDot tone={u.tone} />
              <div className="min-w-0 flex-1">
                <p className="font-mono-tight text-2xl font-medium text-slate-900 leading-none">{u.count}</p>
                <p className="text-[11px] text-slate-500 truncate mt-1">{u.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* KPI grid */}
      <div>
        <p className="text-[10px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-4">Platform metrikleri</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-summary-grid">
          {cards.map((c) => <AdminKpiCard key={c.label} {...c} />)}
        </div>
      </div>

      {/* Payment note */}
      <div className="rounded-2xl bg-amber-50/50 border border-amber-100 p-5 flex items-start gap-3" data-testid="payment-note">
        <Warning size={16} weight="fill" className="text-amber-700 shrink-0 mt-0.5" />
        <div className="text-xs text-amber-900 leading-relaxed">
          <p className="font-medium mb-0.5">Ödeme entegrasyonu henüz aktif değil.</p>
          <p className="text-amber-800/80">Hemen Al ve doping ödeme akışları canlıya alınmadan önce ödeme sağlayıcısı seçilecek. Şu an <code className="bg-white/70 rounded px-1 py-0.5">/api/payments/*</code> intentional 503 döner.</p>
        </div>
      </div>
    </div>
  );
}

function AdminKpiCard({ label, value, sub, testid, tone = "slate" }) {
  return (
    <div className="p-5 rounded-2xl bg-white premium-border relative overflow-hidden" data-testid={testid}>
      <ToneStripe tone={tone} />
      <p className="text-[10px] uppercase tracking-[0.24em] font-medium text-slate-400 mb-3">{label}</p>
      <p className="font-mono-tight text-3xl font-medium text-slate-900 tracking-tight leading-none">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-2 leading-snug">{sub}</p>}
    </div>
  );
}

function ToneDot({ tone }) {
  const cls = { red: "bg-[#B54037]", yellow: "bg-amber-500", green: "bg-emerald-600", blue: "bg-slate-900", slate: "bg-slate-400" }[tone] || "bg-slate-400";
  return <span className={`w-2 h-2 rounded-full ${cls} shrink-0`} />;
}
function ToneStripe({ tone }) {
  const cls = { red: "bg-[#B54037]", yellow: "bg-amber-500", green: "bg-emerald-600", blue: "bg-slate-900", slate: "bg-slate-200" }[tone] || "bg-slate-200";
  return <span className={`absolute top-0 left-0 right-0 h-[3px] ${cls}`} />;
}

function DashboardSkeleton() {
  return (
    <div className="space-y-8">
      <div className="rounded-2xl bg-slate-50 h-32 animate-pulse" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => <div key={i} className="rounded-2xl bg-slate-50 h-32 animate-pulse" />)}
      </div>
    </div>
  );
}

// ============ TAB: GROWTH (Faz 3D) ============
function UpsellKpi({ label, value, testid, highlight }) {
  return (
    <div
      className={`p-4 rounded-xl ${highlight ? "bg-slate-900 text-white" : "bg-white"}`}
      data-testid={testid}
    >
      <p className={`text-[10px] uppercase tracking-[0.24em] font-medium ${highlight ? "text-white/60" : "text-slate-400"} mb-2`}>
        {label}
      </p>
      <p className={`font-mono-tight text-2xl font-medium tracking-tight ${highlight ? "text-white" : "text-slate-900"}`}>
        {value}
      </p>
    </div>
  );
}

function GrowthTab() {
  const [g, setG] = useState(null);
  useEffect(() => { api.get("/admin/growth").then(({ data }) => setG(data)); }, []);
  if (!g) return <p className="text-slate-400">Yükleniyor…</p>;

  const kpis = [
    { label: "Günlük aktif ilan", value: g.active_listings, sub: `${g.listings_24h} son 24 saatte açıldı`, testid: "growth-active" },
    { label: "Aktif doping", value: g.active_doping, sub: "canlı öne çıkarma", testid: "growth-doping" },
    { label: "7 günlük teklif", value: g.bids_7d, sub: `${g.bids_24h} son 24 saatte`, testid: "growth-bids" },
    { label: "Bitmek üzere", value: g.ending_soon, sub: "24 saat içinde", testid: "growth-ending" },
    { label: "7 günlük gelir", value: `₺${Number(g.total_revenue_7d).toLocaleString("tr-TR")}`, sub: `Komisyon: ₺${Number(g.commission_7d).toLocaleString("tr-TR")} · Doping: ₺${Number(g.doping_revenue_7d).toLocaleString("tr-TR")}`, testid: "growth-revenue" },
    { label: "7 günlük hacim", value: `₺${Number(g.volume_7d).toLocaleString("tr-TR")}`, sub: "ödemeli sipariş toplamı", testid: "growth-volume" },
    { label: "Yeni üye (7g)", value: g.new_users_7d, sub: `${g.new_users_30d} son 30 günde`, testid: "growth-new-users" },
    { label: "Tahmini görüntülenme", value: g.views_estimated, sub: "teklif+favori bazlı", testid: "growth-views" },
  ];

  return (
    <div className="space-y-10" data-testid="admin-growth">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-growth-grid">
        {kpis.map((c) => (
          <div key={c.label} className="p-6 rounded-xl bg-slate-50" data-testid={c.testid}>
            <p className="text-[10px] uppercase tracking-[0.24em] font-medium text-slate-400 mb-3">{c.label}</p>
            <p className="font-mono-tight text-3xl font-medium text-slate-900 tracking-tight">{c.value}</p>
            <p className="text-xs text-slate-500 mt-2">{c.sub}</p>
          </div>
        ))}
      </div>

      {/* Doping breakdown */}
      {Object.keys(g.doping_by_type || {}).length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-3">Aktif doping dağılımı</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="growth-doping-breakdown">
            {Object.entries(g.doping_by_type).map(([k, v]) => (
              <div key={k} className="p-4 rounded-xl bg-white flex items-center justify-between">
                <span className="text-xs uppercase tracking-[0.2em] font-medium text-slate-500 flex items-center gap-1.5">
                  <Lightning size={11} weight="fill" className="text-amber-500" /> {k}
                </span>
                <span className="font-mono-tight text-lg font-medium text-slate-900">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upsell funnel (Faz 3D+) */}
      {g.upsell && (
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-3 flex items-center gap-2">
            <Sparkle size={12} weight="fill" className="text-amber-500" />
            Doping Upsell — son {g.upsell.window_days} gün
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="growth-upsell-grid">
            <UpsellKpi label="Gösterim" value={g.upsell.shown} testid="upsell-shown" />
            <UpsellKpi label="Seçim" value={g.upsell.selected} testid="upsell-selected" />
            <UpsellKpi label="Şimdi değil" value={g.upsell.dismissed} testid="upsell-dismissed" />
            <UpsellKpi label="Dönüşüm oranı" value={`%${g.upsell.conversion_rate}`} testid="upsell-conversion" highlight />
          </div>
          {Object.keys(g.upsell.by_package || {}).length > 0 && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="growth-upsell-by-pkg">
              {Object.entries(g.upsell.by_package).map(([k, v]) => (
                <div key={k} className="p-3 rounded-lg bg-white flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-500">{k}</span>
                  <span className="font-mono-tight text-sm font-medium text-slate-900">{v}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* AI Moderation (Faz 4A) */}
      {g.moderation && (
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-3 flex items-center gap-2">
            <ShieldCheck size={12} weight="regular" className="text-emerald-600" />
            AI Moderasyon — son {g.moderation.window_days} gün
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="growth-moderation-grid">
            <UpsellKpi label="Otomatik onay" value={g.moderation.auto_approved} testid="mod-growth-auto" />
            <UpsellKpi label="Manuel" value={g.moderation.manual_review} testid="mod-growth-manual" highlight={g.moderation.manual_review > 0} />
            <UpsellKpi label="Reddedilen" value={g.moderation.rejected} testid="mod-growth-rejected" />
            <UpsellKpi label="Ort. risk" value={g.moderation.avg_risk_score} testid="mod-growth-avg" />
            <UpsellKpi label="Otom. onay oranı" value={`%${g.moderation.auto_approve_rate}`} testid="mod-growth-rate" />
            <UpsellKpi label="AI vs Heur." value={`${g.moderation.ai_reviewed} / ${g.moderation.heuristic_reviewed}`} testid="mod-growth-mix" />
          </div>
        </div>
      )}

      {/* Top categories */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-3 flex items-center gap-2">
            <Fire size={12} weight="fill" className="text-[#B54037]" /> En popüler kategoriler
          </p>
          {g.top_categories.length === 0 ? <p className="text-slate-400 text-sm">Henüz veri yok.</p> : (
            <div className="space-y-2" data-testid="growth-top-categories">
              {g.top_categories.map((c) => (
                <div key={c.category} className="p-4 rounded-xl bg-white flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-900 capitalize">{c.category}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{c.bids} toplam teklif</p>
                  </div>
                  <span className="font-mono-tight text-lg font-medium text-slate-900">{c.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-3 flex items-center gap-2">
            <MapPin size={12} weight="regular" /> En popüler şehirler
          </p>
          {g.top_cities.length === 0 ? <p className="text-slate-400 text-sm">Henüz veri yok.</p> : (
            <div className="space-y-2" data-testid="growth-top-cities">
              {g.top_cities.map((c) => (
                <div key={c.city} className="p-4 rounded-xl bg-white flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-900">{c.city}</p>
                  <span className="font-mono-tight text-lg font-medium text-slate-900">{c.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Top listings */}
      <div>
        <p className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-3 flex items-center gap-2">
          <ClockCounterClockwise size={12} weight="regular" /> En çok teklif alan aktif ilanlar
        </p>
        {g.top_listings.length === 0 ? <p className="text-slate-400 text-sm">Henüz veri yok.</p> : (
          <div className="space-y-2" data-testid="growth-top-listings">
            {g.top_listings.map((a) => (
              <Link key={a.id} to={`/mezat/${a.id}`}
                className="p-4 rounded-xl bg-white flex items-center gap-4 hover:bg-slate-50 transition-colors block">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 truncate">{a.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{a.bid_count} teklif · {a.favorites_count || 0} favori</p>
                </div>
                <p className="font-mono-tight text-lg font-medium text-slate-900">₺{Number(a.current_price).toLocaleString("tr-TR")}</p>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ TAB: MODERATION (Faz 4A — AI Onay Kuyruğu) ============
function ModerationTab() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [levelFilter, setLevelFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [logs, setLogs] = useState([]);
  const [checked, setChecked] = useState(new Set());

  const load = () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (levelFilter) q.set("level", levelFilter);
    if (statusFilter) q.set("status", statusFilter);
    Promise.all([
      api.get("/admin/moderation/stats"),
      api.get(`/admin/moderation/queue?${q.toString()}`),
    ]).then(([s, i]) => {
      setStats(s.data); setItems(i.data);
      setChecked(new Set());
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [levelFilter, statusFilter]);

  const override = async (id, new_status, note = "") => {
    try {
      await api.post(`/admin/moderation/${id}/override`, { new_status, note });
      toast.success("Karar güncellendi");
      load();
      if (selected?.id === id) setSelected(null);
    } catch (e) { toast.error(e.response?.data?.detail || "İşlem başarısız"); }
  };

  const bulkOverride = async (new_status, note = "") => {
    if (checked.size === 0) return toast.error("Önce ilan seç");
    if (!window.confirm(`${checked.size} ilan için karar uygulanacak. Onaylıyor musun?`)) return;
    try {
      const { data } = await api.post("/admin/moderation/bulk-override", {
        listing_ids: Array.from(checked), new_status, note,
      });
      toast.success(`${data.updated}/${data.requested} ilan güncellendi`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Toplu işlem başarısız"); }
  };

  const toggleCheck = (id) => {
    setChecked((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const toggleAll = () => {
    if (checked.size === items.length) setChecked(new Set());
    else setChecked(new Set(items.map((i) => i.id)));
  };

  const openDetail = async (item) => {
    setSelected(item);
    try {
      const { data } = await api.get(`/admin/moderation/${item.id}/logs`);
      setLogs(data);
    } catch { setLogs([]); }
  };

  const pendingManualCount = stats?.manual_review || 0;

  const kpis = stats ? [
    { label: "Otomatik onay", value: stats.auto_approved, testid: "mod-kpi-auto" },
    { label: "Manuel inceleme", value: stats.manual_review, testid: "mod-kpi-manual", highlight: stats.manual_review > 0 },
    { label: "Reddedilen", value: stats.rejected, testid: "mod-kpi-rejected" },
    { label: "Ort. risk skoru", value: stats.avg_risk_score, testid: "mod-kpi-avg" },
    { label: "AI ile incelenen", value: stats.ai_reviewed, testid: "mod-kpi-ai" },
    { label: "Heuristic fallback", value: stats.heuristic_reviewed, testid: "mod-kpi-heur" },
  ] : [];

  return (
    <div className="space-y-6" data-testid="admin-moderation">
      {/* Manual review alert */}
      {pendingManualCount > 0 && (
        <div
          className="p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-between gap-3"
          data-testid="mod-manual-alert"
        >
          <div className="flex items-center gap-3">
            <ShieldWarning size={20} weight="fill" className="text-amber-700 shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-900">
                {pendingManualCount} ilan manuel inceleme bekliyor
              </p>
              <p className="text-xs text-amber-800 mt-0.5">
                AI orta risk verdi — inceleyip karar vermen gerekiyor.
              </p>
            </div>
          </div>
          <button
            onClick={() => { setStatusFilter("pending_manual_review"); setLevelFilter(""); }}
            data-testid="mod-manual-alert-cta"
            className="text-xs font-medium bg-amber-900 text-white px-3 py-1.5 rounded-lg hover:bg-amber-800 transition-colors whitespace-nowrap"
          >
            Kuyruğu aç →
          </button>
        </div>
      )}

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="mod-kpi-grid">
          {kpis.map((c) => (
            <div key={c.label} data-testid={c.testid}
              className={`p-4 rounded-xl ${c.highlight ? "bg-slate-900 text-white" : "bg-slate-50"}`}>
              <p className={`text-[10px] uppercase tracking-[0.24em] font-medium mb-2 ${c.highlight ? "text-white/60" : "text-slate-400"}`}>{c.label}</p>
              <p className={`font-mono-tight text-2xl font-medium tracking-tight ${c.highlight ? "text-white" : "text-slate-900"}`}>{c.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-[10px] uppercase tracking-[0.24em] font-medium text-slate-400 mr-2">Seviye:</span>
        {["", "low", "medium", "high"].map((l) => (
          <button key={l || "all"}
            onClick={() => setLevelFilter(l)}
            data-testid={`mod-filter-level-${l || "all"}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${levelFilter === l ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}>
            {l === "" ? "Tümü" : l === "low" ? "Düşük" : l === "medium" ? "Orta" : "Yüksek"}
          </button>
        ))}
        <span className="text-[10px] uppercase tracking-[0.24em] font-medium text-slate-400 mx-2 ml-4">Durum:</span>
        {[
          { v: "", l: "Kuyruk" },
          { v: "pending_manual_review", l: "Manuel" },
          { v: "rejected_by_ai", l: "AI Ret" },
          { v: "high_risk_review", l: "Yüksek Risk" },
          { v: "approved", l: "Onaylı" },
        ].map((s) => (
          <button key={s.v || "all"}
            onClick={() => setStatusFilter(s.v)}
            data-testid={`mod-filter-status-${s.v || "queue"}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${statusFilter === s.v ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}>
            {s.l}
          </button>
        ))}
      </div>

      {/* Bulk actions bar */}
      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 p-3 rounded-xl bg-slate-50" data-testid="mod-bulk-bar">
          <label className="inline-flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              data-testid="mod-bulk-select-all"
              checked={checked.size === items.length && items.length > 0}
              onChange={toggleAll}
              className="rounded border-slate-300"
            />
            {checked.size === 0 ? "Tümünü seç" : `${checked.size} seçildi`}
          </label>
          <div className="flex-1" />
          <button disabled={checked.size === 0} onClick={() => bulkOverride("approved", "Toplu admin onayı")}
            data-testid="mod-bulk-approve"
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-40">
            Seçileni Onayla
          </button>
          <button disabled={checked.size === 0} onClick={() => bulkOverride("rejected_by_admin", "Toplu reddedildi")}
            data-testid="mod-bulk-reject"
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[#B54037] text-white hover:bg-red-800 transition-colors disabled:opacity-40">
            Seçileni Reddet
          </button>
        </div>
      )}

      {/* Queue table */}
      {loading ? (
        <p className="text-slate-400 py-6">Yükleniyor…</p>
      ) : items.length === 0 ? (
        <div className="py-16 text-center bg-slate-50 rounded-xl" data-testid="mod-empty">
          <ShieldCheck size={26} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Bu kriterlerde bekleyen ilan yok.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="mod-queue">
          {items.map((it) => (
            <ModerationRow
              key={it.id} item={it}
              checked={checked.has(it.id)}
              onCheck={() => toggleCheck(it.id)}
              onOpen={() => openDetail(it)}
              onApprove={() => override(it.id, "approved", "Admin onayladı")}
              onReject={() => override(it.id, "rejected_by_admin", "Kural ihlali")}
              onHold={() => override(it.id, "pending_manual_review", "Tekrar incelemede")}
              onHighRisk={() => override(it.id, "high_risk_review", "Yüksek risk kuyruğuna alındı")}
            />
          ))}
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setSelected(null)} data-testid="mod-detail-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto soft-shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b divider flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.24em] text-slate-400">İlan #{selected.id.slice(0, 8)}</p>
                <h3 className="font-display text-xl font-medium tracking-tight text-slate-900 mt-1 truncate">{selected.title}</h3>
              </div>
              <button onClick={() => setSelected(null)} className="p-2 rounded-lg hover:bg-slate-50" data-testid="mod-detail-close">
                <X size={16} weight="bold" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="Satıcı" value={selected.seller_name} />
                <Info label="Kategori" value={selected.category} />
                <Info label="Fiyat" value={`₺${Number(selected.starting_price).toLocaleString("tr-TR")}`} />
                <Info label="Skor" value={selected.ai_review_score} />
                <Info label="Seviye" value={<LevelBadge level={selected.ai_review_level} />} />
                <Info label="Karar" value={selected.ai_review_decision} />
                <Info label="Kaynak" value={selected.moderation_source === "ai" ? "AI (LLM)" : "Heuristic"} />
                <Info label="Tarih" value={new Date(selected.ai_reviewed_at).toLocaleString("tr-TR")} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.24em] text-slate-400 mb-2">Risk nedenleri</p>
                <ul className="space-y-1.5 text-sm text-slate-700">
                  {(selected.ai_review_reasons || []).map((r, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[#B54037] mt-0.5">•</span> <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.24em] text-slate-400 mb-2">Açıklama</p>
                <p className="text-sm text-slate-600 leading-relaxed max-h-40 overflow-y-auto">{selected.description}</p>
              </div>
              {logs.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.24em] text-slate-400 mb-2">Denetim geçmişi</p>
                  <div className="space-y-1.5">
                    {logs.map((l) => (
                      <div key={l.id} className="text-xs bg-slate-50 rounded-lg p-2.5 flex items-center justify-between gap-2">
                        <span><strong className="capitalize">{l.actor_type}</strong> · {l.action}</span>
                        <span className="text-slate-400">{l.old_status} → {l.new_status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="border-t divider p-4 flex flex-wrap gap-2 justify-end">
              <button onClick={() => override(selected.id, "approved", "Admin onayladı")}
                data-testid="mod-detail-approve"
                className="px-4 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors">
                Onayla
              </button>
              <button onClick={() => override(selected.id, "pending_manual_review", "Tekrar incele")}
                data-testid="mod-detail-hold"
                className="px-4 py-2 rounded-lg text-sm font-medium bg-white border border-slate-200 text-slate-700 hover:border-slate-900 transition-colors">
                Beklet
              </button>
              <button onClick={() => override(selected.id, "high_risk_review", "Yüksek risk kuyruğu")}
                data-testid="mod-detail-highrisk"
                className="px-4 py-2 rounded-lg text-sm font-medium bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors">
                Yüksek Risk
              </button>
              <button onClick={() => override(selected.id, "rejected_by_admin", "Kural ihlali")}
                data-testid="mod-detail-reject"
                className="px-4 py-2 rounded-lg text-sm font-medium bg-[#B54037] text-white hover:bg-red-800 transition-colors">
                Reddet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ModerationRow({ item, checked, onCheck, onOpen, onApprove, onReject, onHold, onHighRisk }) {
  return (
    <div className="p-4 rounded-xl bg-white border border-slate-100 flex flex-col md:flex-row md:items-center gap-4"
         data-testid={`mod-row-${item.id}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onCheck}
        data-testid={`mod-check-${item.id}`}
        className="mt-1 md:mt-0 rounded border-slate-300 shrink-0"
        onClick={(e) => e.stopPropagation()}
      />
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onOpen}>
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <LevelBadge level={item.ai_review_level} />
          <StatusBadge status={item.approval_status} />
          <span className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-400">
            {item.moderation_source === "ai" ? "AI" : "Heuristic"}
          </span>
          <span className="font-mono-tight text-xs text-slate-500">Skor {item.ai_review_score}</span>
        </div>
        <p className="font-medium text-slate-900 truncate">{item.title}</p>
        <p className="text-xs text-slate-500 truncate mt-0.5">
          {item.seller_name} · {item.category} · ₺{Number(item.starting_price).toLocaleString("tr-TR")}
        </p>
        {item.ai_review_reasons?.length > 0 && (
          <p className="text-xs text-slate-500 truncate mt-1 italic">
            {item.ai_review_reasons.slice(0, 2).join(" · ")}
          </p>
        )}
      </div>
      <div className="flex flex-wrap gap-2 shrink-0">
        <button onClick={onApprove} data-testid={`mod-approve-${item.id}`}
          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors">
          Onayla
        </button>
        <button onClick={onHold} data-testid={`mod-hold-${item.id}`}
          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-slate-200 text-slate-700 hover:border-slate-900 transition-colors">
          Beklet
        </button>
        <button onClick={onHighRisk} data-testid={`mod-highrisk-${item.id}`}
          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors">
          Y. Risk
        </button>
        <button onClick={onReject} data-testid={`mod-reject-${item.id}`}
          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[#B54037] text-white hover:bg-red-800 transition-colors">
          Reddet
        </button>
      </div>
    </div>
  );
}

function LevelBadge({ level }) {
  const map = {
    low: { l: "Düşük", tone: "bg-emerald-50 text-emerald-800" },
    medium: { l: "Orta", tone: "bg-amber-50 text-amber-800" },
    high: { l: "Yüksek", tone: "bg-red-50 text-[#B54037]" },
  };
  const c = map[level] || { l: level || "—", tone: "bg-slate-100 text-slate-700" };
  return (
    <span className={`inline-flex items-center text-[10px] uppercase tracking-[0.2em] font-medium px-2.5 py-1 rounded-full ${c.tone}`}>
      {c.l}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    approved: { l: "Onaylı", tone: "bg-emerald-50 text-emerald-800" },
    pending_manual_review: { l: "Manuel", tone: "bg-amber-50 text-amber-800" },
    rejected_by_ai: { l: "AI Ret", tone: "bg-red-50 text-[#B54037]" },
    rejected_by_admin: { l: "Admin Ret", tone: "bg-red-50 text-[#B54037]" },
    high_risk_review: { l: "Y. Risk", tone: "bg-amber-50 text-amber-800" },
  };
  const c = map[status] || { l: status, tone: "bg-slate-100 text-slate-700" };
  return (
    <span className={`inline-flex items-center text-[10px] uppercase tracking-[0.2em] font-medium px-2.5 py-1 rounded-full ${c.tone}`}>
      {c.l}
    </span>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.24em] text-slate-400 mb-1">{label}</p>
      <div className="text-sm text-slate-900 font-medium">{value}</div>
    </div>
  );
}

// ============ TAB: USERS ============
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (role) params.role = role;
      const { data } = await api.get("/admin/users", { params });
      setUsers(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [role]);

  const patch = async (uid, body) => {
    try {
      const { data } = await api.patch(`/admin/users/${uid}`, body);
      setUsers((prev) => prev.map((u) => u.user_id === uid ? data.user : u));
      if (selected?.user.user_id === uid) setSelected((prev) => ({ ...prev, user: data.user }));
      toast.success("Güncellendi");
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  const openDetail = async (uid) => {
    const { data } = await api.get(`/admin/users/${uid}`);
    setSelected(data);
  };

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div className="flex gap-2 flex-wrap">
        <div className="flex items-center bg-slate-50 rounded-lg flex-1 min-w-[220px]">
          <MagnifyingGlass size={15} className="ml-3 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Ad, e-posta veya user_id..."
            className="w-full bg-transparent px-3 py-2.5 outline-none text-sm text-slate-800"
            data-testid="admin-users-search" />
        </div>
        <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="admin-users-role"
          className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-slate-900">
          <option value="">Tüm roller</option>
          <option value="individual">Bireysel</option>
          <option value="corporate">Kurumsal</option>
          <option value="admin">Admin</option>
        </select>
        <button onClick={load} className="bg-slate-900 text-white px-5 py-2.5 rounded-lg font-medium text-sm hover:bg-slate-800 transition-colors">Ara</button>
      </div>

      {loading ? <p className="text-slate-400">Yükleniyor…</p> : (
        <div className="rounded-xl overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-slate-500">
                <th className="p-3">Kullanıcı</th>
                <th className="p-3">Rol</th>
                <th className="p-3">Durum</th>
                <th className="p-3">Puan</th>
                <th className="p-3 text-right">Aksiyon</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-t divider" data-testid={`admin-user-row-${u.user_id}`}>
                  <td className="p-3">
                    <p className="font-medium text-slate-900">{u.name}</p>
                    <p className="text-xs text-slate-400">{u.email}</p>
                  </td>
                  <td className="p-3">
                    <span className="text-xs uppercase tracking-[0.2em] font-medium text-slate-600">{u.role}</span>
                  </td>
                  <td className="p-3">
                    <div className="flex flex-col gap-1 text-[11px]">
                      {u.is_banned && <span className="text-[#B54037]">Banlı</span>}
                      {u.is_suspended && <span className="text-amber-700">Askıda</span>}
                      {u.is_verified ? <span className="text-emerald-700">Doğrulu</span> : <span className="text-slate-400">Doğrulanmamış</span>}
                    </div>
                  </td>
                  <td className="p-3 font-mono-tight text-slate-700">
                    {u.review_count > 0 ? `⭐ ${u.seller_rating?.toFixed(1)}` : "-"}
                  </td>
                  <td className="p-3 text-right space-x-1">
                    <button onClick={() => openDetail(u.user_id)}
                      className="text-xs font-medium text-slate-600 hover:text-slate-900 px-2 py-1 rounded"
                      data-testid={`admin-user-detail-${u.user_id}`}>Detay</button>
                    <button onClick={() => patch(u.user_id, { is_banned: !u.is_banned })}
                      className={`text-xs font-medium px-2 py-1 rounded ${u.is_banned ? "text-emerald-700 hover:bg-emerald-50" : "text-[#B54037] hover:bg-red-50"}`}
                      data-testid={`admin-user-ban-${u.user_id}`}>
                      {u.is_banned ? "Ban kaldır" : "Banla"}
                    </button>
                    <button onClick={() => patch(u.user_id, { is_suspended: !u.is_suspended })}
                      className="text-xs font-medium text-amber-700 hover:bg-amber-50 px-2 py-1 rounded"
                      data-testid={`admin-user-suspend-${u.user_id}`}>
                      {u.is_suspended ? "Askıyı kaldır" : "Askıya al"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && <p className="text-center text-slate-400 py-12">Kullanıcı bulunamadı.</p>}
        </div>
      )}

      {selected && <UserDetailModal detail={selected} onClose={() => setSelected(null)} onPatch={patch} />}
    </div>
  );
}

function UserDetailModal({ detail, onClose, onPatch }) {
  const u = detail.user;
  const [note, setNote] = useState(u.admin_note || "");
  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-8 soft-shadow-lg" onClick={(e) => e.stopPropagation()} data-testid="admin-user-modal">
        <p className="text-[10px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-2">Kullanıcı detayı</p>
        <h2 className="font-display text-2xl font-medium tracking-tight text-slate-900 mb-1">{u.name}</h2>
        <p className="text-sm text-slate-500 mb-6">{u.email}</p>

        <div className="grid grid-cols-3 gap-3 mb-6">
          <Stat label="İlanlar" value={detail.listings_count} />
          <Stat label="Teklifler" value={detail.bids_count} />
          <Stat label="Şikayet" value={detail.reports_against} />
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.24em] font-medium text-slate-400 mb-2 block">Rol</label>
            <select value={u.role || "individual"} onChange={(e) => onPatch(u.user_id, { role: e.target.value })}
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900">
              <option value="individual">Bireysel</option>
              <option value="corporate">Kurumsal</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.24em] font-medium text-slate-400 mb-2 block">Admin notu</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-slate-900" />
            <button onClick={() => onPatch(u.user_id, { admin_note: note })}
              className="mt-2 bg-slate-900 text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-slate-800 transition-colors">Notu kaydet</button>
          </div>
        </div>

        <button onClick={onClose} className="mt-6 w-full py-2.5 rounded-lg font-medium text-slate-700 hover:bg-slate-50 transition-colors">Kapat</button>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="p-3 rounded-lg bg-slate-50">
      <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">{label}</p>
      <p className="font-mono-tight text-xl font-medium text-slate-900 mt-1">{value}</p>
    </div>
  );
}

// ============ TAB: LISTINGS ============
function ListingsTab() {
  const [listings, setListings] = useState([]);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.filter = filter;
      if (q) params.q = q;
      const { data } = await api.get("/admin/listings", { params });
      setListings(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const moderate = async (aid, action) => {
    try {
      await api.patch(`/admin/listings/${aid}/moderate`, { action });
      toast.success(`İlan ${action === "approve" ? "onaylandı" : action === "reject" ? "reddedildi" : action === "remove" ? "kaldırıldı" : "flag'leri temizlendi"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  const filters = [
    { id: "", label: "Hepsi" },
    { id: "pending", label: "Bekleyen" },
    { id: "approved", label: "Onaylı" },
    { id: "rejected", label: "Reddedilmiş" },
    { id: "reported", label: "Şikayetli" },
    { id: "risky", label: "Riskli" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-listings">
      <div className="flex flex-wrap gap-2">
        {filters.map((f) => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${filter === f.id ? "bg-slate-900 text-white" : "bg-slate-50 text-slate-600 hover:bg-slate-100"}`}
            data-testid={`admin-listings-filter-${f.id || "all"}`}>
            {f.label}
          </button>
        ))}
        <div className="flex items-center bg-slate-50 rounded-lg flex-1 min-w-[220px]">
          <MagnifyingGlass size={15} className="ml-3 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Başlık veya satıcı..." className="w-full bg-transparent px-3 py-2.5 outline-none text-sm text-slate-800"
            data-testid="admin-listings-search" />
        </div>
      </div>

      {loading ? <p className="text-slate-400">Yükleniyor…</p> : (
        <div className="space-y-2">
          {listings.map((a) => (
            <div key={a.id} className="p-4 rounded-xl bg-white flex items-center gap-4 hover:bg-slate-50 transition-colors" data-testid={`admin-listing-row-${a.id}`}>
              <img src={a.images?.[0] ? (a.images[0].startsWith("http") ? a.images[0] : `${process.env.REACT_APP_BACKEND_URL}/api/files/${a.images[0]}`) : "https://images.unsplash.com/photo-1611095973362-88e8e2557e58?w=200&q=80"}
                alt="" className="w-16 h-16 rounded-lg object-cover flex-none bg-slate-100" />
              <div className="flex-1 min-w-0">
                <Link to={`/mezat/${a.id}`} className="font-medium text-slate-900 hover:underline truncate block">{a.title}</Link>
                <p className="text-xs text-slate-500 mt-0.5">{a.seller_name} · {a.category} · ₺{Number(a.current_price).toLocaleString("tr-TR")}</p>
                <div className="flex gap-1.5 mt-1.5 flex-wrap">
                  <StatusPill status={a.approval_status || "approved"} />
                  {a.status && <StatusPill status={a.status} />}
                  {a.report_count > 0 && <span className="text-[10px] uppercase tracking-[0.18em] font-medium px-2 py-0.5 rounded bg-red-50 text-[#B54037]">{a.report_count} şikayet</span>}
                  {(a.risk_flags || []).map((f) => (
                    <span key={f} className="text-[10px] uppercase tracking-[0.18em] font-medium px-2 py-0.5 rounded bg-amber-50 text-amber-800">{f}</span>
                  ))}
                </div>
              </div>
              <div className="flex gap-1 flex-none">
                <button onClick={() => moderate(a.id, "approve")} title="Onayla"
                  className="p-2 rounded-lg text-emerald-700 hover:bg-emerald-50 transition-colors" data-testid={`admin-approve-${a.id}`}>
                  <Check size={16} weight="bold" />
                </button>
                <button onClick={() => moderate(a.id, "reject")} title="Reddet"
                  className="p-2 rounded-lg text-amber-700 hover:bg-amber-50 transition-colors" data-testid={`admin-reject-${a.id}`}>
                  <X size={16} weight="bold" />
                </button>
                <button onClick={() => moderate(a.id, "remove")} title="Kaldır"
                  className="p-2 rounded-lg text-[#B54037] hover:bg-red-50 transition-colors" data-testid={`admin-remove-${a.id}`}>
                  <Trash size={16} weight="bold" />
                </button>
              </div>
            </div>
          ))}
          {listings.length === 0 && <p className="text-center text-slate-400 py-12">Bu filtreye uyan ilan yok.</p>}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: "bg-amber-50 text-amber-800",
    approved: "bg-emerald-50 text-emerald-800",
    rejected: "bg-red-50 text-[#B54037]",
    removed: "bg-slate-100 text-slate-500",
    active: "bg-emerald-50 text-emerald-800",
    closed: "bg-slate-100 text-slate-500",
  };
  return (
    <span className={`text-[10px] uppercase tracking-[0.18em] font-medium px-2 py-0.5 rounded ${map[status] || "bg-slate-100 text-slate-600"}`}>
      {status}
    </span>
  );
}

// ============ TAB: AUCTIONS ============
function AuctionsTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("active");
  const [selected, setSelected] = useState(null);
  const [bids, setBids] = useState([]);

  useEffect(() => {
    api.get("/admin/auctions", { params: { filter } }).then(({ data }) => setItems(data));
  }, [filter]);

  const openBids = async (aid) => {
    const a = items.find((x) => x.id === aid);
    setSelected(a);
    const { data } = await api.get(`/admin/auctions/${aid}/bids`);
    setBids(data);
  };

  return (
    <div className="space-y-6" data-testid="admin-auctions">
      <div className="flex gap-2 flex-wrap">
        {[
          { id: "active", label: "Aktif" },
          { id: "ending_soon", label: "Yakında bitiyor" },
          { id: "closed", label: "Kapanmış" },
          { id: "with_winner", label: "Kazananı olan" },
        ].map((f) => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${filter === f.id ? "bg-slate-900 text-white" : "bg-slate-50 text-slate-600 hover:bg-slate-100"}`}
            data-testid={`admin-auctions-filter-${f.id}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {items.map((a) => (
          <div key={a.id} className="p-4 rounded-xl bg-white flex items-center gap-4" data-testid={`admin-auction-row-${a.id}`}>
            <div className="flex-1 min-w-0">
              <Link to={`/mezat/${a.id}`} className="font-medium text-slate-900 hover:underline truncate block">{a.title}</Link>
              <p className="text-xs text-slate-500 mt-0.5">
                Satıcı: {a.seller_name} · Min artış: ₺{a.min_increment} · Hemen al: ₺{a.buy_now_price || "-"}
              </p>
              <div className="flex gap-2 mt-1 text-[11px]">
                <span>Güncel: <span className="font-mono-tight font-medium text-slate-900">₺{Number(a.current_price).toLocaleString("tr-TR")}</span></span>
                <span className="text-slate-300">·</span>
                <span>{a.bid_count} teklif</span>
                {a.highest_bidder_name && <><span className="text-slate-300">·</span><span>En yüksek: {a.highest_bidder_name}</span></>}
                {a.winner_id && <><span className="text-slate-300">·</span><span className="text-emerald-700">Kazanan: {a.highest_bidder_name}</span></>}
              </div>
            </div>
            <button onClick={() => openBids(a.id)} className="text-xs font-medium text-slate-600 hover:text-slate-900 px-3 py-2 rounded-lg hover:bg-slate-50"
              data-testid={`admin-view-bids-${a.id}`}>Teklifler</button>
          </div>
        ))}
        {items.length === 0 && <p className="text-center text-slate-400 py-12">Bu filtreye uyan açık artırma yok.</p>}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-8 soft-shadow-lg" onClick={(e) => e.stopPropagation()} data-testid="admin-bids-modal">
            <p className="text-[10px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-2">Teklif geçmişi</p>
            <h2 className="font-display text-xl font-medium tracking-tight text-slate-900 mb-6">{selected.title}</h2>
            {bids.length === 0 ? <p className="text-slate-400 text-sm">Henüz teklif yok.</p> : bids.map((b, i) => (
              <div key={b.id} className="flex items-center justify-between py-3 border-b divider last:border-0">
                <div>
                  <p className="text-sm font-medium text-slate-900">{b.user_name}</p>
                  {i === 0 && <p className="text-[10px] uppercase tracking-[0.24em] font-medium text-emerald-700 mt-0.5">En yüksek</p>}
                </div>
                <div className="text-right">
                  <p className="font-mono-tight font-medium text-slate-900">₺{Number(b.amount).toLocaleString("tr-TR")}</p>
                  <p className="text-[11px] text-slate-400 font-mono-tight">{new Date(b.timestamp).toLocaleString("tr-TR")}</p>
                </div>
              </div>
            ))}
            <button onClick={() => setSelected(null)} className="mt-6 w-full py-2.5 rounded-lg font-medium text-slate-700 hover:bg-slate-50">Kapat</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ TAB: COMPLAINTS ============
function ComplaintsTab() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("open");

  const load = async () => {
    const params = {};
    if (status) params.status = status;
    const { data } = await api.get("/admin/complaints", { params });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const setStat = async (cid, newStatus) => {
    try {
      await api.patch(`/admin/complaints/${cid}`, { status: newStatus });
      toast.success("Durum güncellendi");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-complaints">
      <div className="flex gap-2 flex-wrap">
        {[
          { id: "open", label: "Açık" },
          { id: "reviewing", label: "İnceleniyor" },
          { id: "resolved", label: "Çözüldü" },
          { id: "dismissed", label: "Reddedildi" },
          { id: "", label: "Hepsi" },
        ].map((f) => (
          <button key={f.id} onClick={() => setStatus(f.id)}
            className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${status === f.id ? "bg-slate-900 text-white" : "bg-slate-50 text-slate-600 hover:bg-slate-100"}`}
            data-testid={`admin-complaints-filter-${f.id || "all"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {items.map((c) => (
          <div key={c.id} className="p-4 rounded-xl bg-white" data-testid={`admin-complaint-${c.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.24em] font-medium text-slate-400">
                  {c.target_type} · {c.target_type === "listing" ? (
                    <Link to={`/mezat/${c.target_id}`} className="text-slate-700 hover:underline">{c.target_id.slice(0, 8)}...</Link>
                  ) : c.target_id.slice(0, 12)}
                </p>
                <p className="font-medium text-slate-900 mt-1">{c.reason}</p>
                {c.message && <p className="text-sm text-slate-600 mt-1">{c.message}</p>}
                <p className="text-xs text-slate-400 mt-2">
                  Şikayet eden: {c.reporter_name} · {new Date(c.created_at).toLocaleString("tr-TR")}
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <StatusPill status={c.status} />
                <select value={c.status} onChange={(e) => setStat(c.id, e.target.value)}
                  className="mt-2 bg-white border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-slate-900"
                  data-testid={`admin-complaint-status-${c.id}`}>
                  <option value="open">Açık</option>
                  <option value="reviewing">İnceleniyor</option>
                  <option value="resolved">Çözüldü</option>
                  <option value="dismissed">Reddedildi</option>
                </select>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-center text-slate-400 py-12">Bu durumda şikayet yok.</p>}
      </div>
    </div>
  );
}

// ============ TAB: FINANCE ============
function FinanceTab() {
  const [f, setF] = useState(null);
  const [orders, setOrders] = useState([]);
  useEffect(() => {
    api.get("/admin/finance").then(({ data }) => setF(data));
    api.get("/admin/orders").then(({ data }) => setOrders(data));
  }, []);
  if (!f) return <p className="text-slate-400">Yükleniyor…</p>;

  const cards = [
    { label: "İşlem hacmi", value: `₺${Number(f.total_volume).toLocaleString("tr-TR")}` },
    { label: "Komisyon geliri", value: `₺${Number(f.commission_earned).toLocaleString("tr-TR")}`,
      sub: `Oran: %${(f.commission_rate * 100).toFixed(1)}` },
    { label: "Doping geliri", value: `₺${Number(f.doping_revenue).toLocaleString("tr-TR")}` },
    { label: "Brüt gelir", value: `₺${Number(f.gross_revenue).toLocaleString("tr-TR")}`,
      sub: "komisyon + doping" },
    { label: "Satıcı hakedişi", value: `₺${Number(f.seller_payouts_total).toLocaleString("tr-TR")}` },
    { label: "Bekleyen ödeme", value: f.pending_payouts, sub: "sipariş adedi" },
    { label: "Havuzda bekleyen", value: f.escrow_held },
    { label: "Serbest bırakılmış", value: f.escrow_released },
  ];

  return (
    <div className="space-y-8" data-testid="admin-finance">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-finance-grid">
        {cards.map((c) => (
          <div key={c.label} className="p-6 rounded-xl bg-slate-50">
            <p className="text-[10px] uppercase tracking-[0.24em] font-medium text-slate-400 mb-3">{c.label}</p>
            <p className="font-mono-tight text-2xl font-medium text-slate-900 tracking-tight">{c.value}</p>
            {c.sub && <p className="text-xs text-slate-500 mt-2">{c.sub}</p>}
          </div>
        ))}
      </div>

      <div>
        <p className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-3">Son siparişler</p>
        {orders.length === 0 ? <p className="text-slate-400 text-sm py-6">Henüz sipariş yok.</p> : (
          <div className="space-y-2" data-testid="admin-orders-list">
            {orders.slice(0, 20).map((o) => (
              <div key={o.id} className="p-4 rounded-xl bg-white flex items-center gap-4 flex-wrap" data-testid={`admin-order-${o.id}`}>
                <div className="flex-1 min-w-0">
                  <Link to={`/mezat/${o.listing_id}`} className="font-medium text-slate-900 hover:underline truncate block">{o.listing_title}</Link>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Alıcı: {o.buyer_id.slice(0, 12)} · Satıcı: {o.seller_id.slice(0, 12)}
                  </p>
                  <div className="flex gap-1.5 mt-1.5 text-[10px] uppercase tracking-[0.2em]">
                    <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">{o.status}</span>
                    <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">Havuz: {o.escrow_status}</span>
                    <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">Ödeme: {o.payout_status}</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-mono-tight font-medium text-slate-900">₺{Number(o.amount).toLocaleString("tr-TR")}</p>
                  <p className="text-[11px] text-slate-500">Kom: ₺{Number(o.platform_fee).toLocaleString("tr-TR")}</p>
                  <p className="text-[11px] text-slate-500">Satıcı: ₺{Number(o.seller_payout).toLocaleString("tr-TR")}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ TAB: DOPING ============
function DopingTab() {
  const [pkgs, setPkgs] = useState([]);
  const load = async () => {
    const { data } = await api.get("/admin/doping-packages");
    setPkgs(data);
  };
  useEffect(() => { load(); }, []);
  const update = async (id, patch) => {
    try {
      await api.patch(`/admin/doping-packages/${id}`, patch);
      toast.success("Paket güncellendi");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  return (
    <div className="space-y-3" data-testid="admin-doping">
      {pkgs.map((p) => (
        <div key={p.id} className="p-5 rounded-xl bg-white" data-testid={`admin-doping-${p.id}`}>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <p className="text-[10px] uppercase tracking-[0.24em] font-medium text-slate-400">{p.id}</p>
              <p className="font-display text-lg font-medium tracking-tight text-slate-900 mt-1">{p.label}</p>
              <p className="text-sm text-slate-500 mt-1 max-w-md">{p.description}</p>
            </div>
            <div className="flex gap-3 items-end">
              <div>
                <label className="text-[10px] uppercase tracking-[0.24em] font-medium text-slate-400">Fiyat ₺</label>
                <input type="number" defaultValue={p.price}
                  onBlur={(e) => e.target.value != p.price && update(p.id, { price: parseFloat(e.target.value) })}
                  data-testid={`doping-price-${p.id}`}
                  className="w-24 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono-tight focus:outline-none focus:border-slate-900 transition-colors" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.24em] font-medium text-slate-400">Gün</label>
                <input type="number" defaultValue={p.days}
                  onBlur={(e) => e.target.value != p.days && update(p.id, { days: parseInt(e.target.value) })}
                  data-testid={`doping-days-${p.id}`}
                  className="w-20 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono-tight focus:outline-none focus:border-slate-900 transition-colors" />
              </div>
              <button onClick={() => update(p.id, { active: !p.active })}
                data-testid={`doping-toggle-${p.id}`}
                className={`px-4 py-2 rounded-lg text-xs font-medium transition-colors ${p.active ? "bg-emerald-50 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>
                {p.active ? "Aktif" : "Pasif"}
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ TAB: SETTINGS ============
function SettingsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)); }, []);
  const patch = async (body) => {
    try {
      const { data } = await api.patch("/admin/settings", body);
      setS(data);
      toast.success("Ayarlar güncellendi");
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };
  if (!s) return <p className="text-slate-400">Yükleniyor…</p>;

  return (
    <div className="space-y-6 max-w-xl" data-testid="admin-settings">
      <Field label="Platform komisyon oranı (%)"
        hint="Sipariş tutarından kesilen platform komisyonu. 0.08 = %8"
        value={s.commission_rate * 100}
        step="0.5"
        onSave={(v) => patch({ commission_rate: parseFloat(v) / 100 })}
        testid="setting-commission" />
      <Field label="Havuz serbest bırakma süresi (gün)"
        hint="Alıcı onaylamazsa kaç gün sonra ödeme satıcıya aktarılsın"
        value={s.escrow_release_days}
        onSave={(v) => patch({ escrow_release_days: parseInt(v) })}
        testid="setting-escrow-days" />
      <Field label="Anti-sniper uzatma süresi (saniye)"
        hint="Son X saniyede gelen tekliflerde açık artırma otomatik uzatılır"
        value={s.anti_sniper_extend_seconds}
        onSave={(v) => patch({ anti_sniper_extend_seconds: parseInt(v) })}
        testid="setting-anti-sniper" />
    </div>
  );
}

function Field({ label, hint, value, step, onSave, testid }) {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  return (
    <div className="p-5 rounded-xl bg-white">
      <label className="text-[10px] uppercase tracking-[0.24em] font-medium text-slate-400 block mb-2">{label}</label>
      <div className="flex gap-2 items-center">
        <input type="number" value={v} onChange={(e) => setV(e.target.value)} step={step || "1"}
          data-testid={testid}
          className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm font-mono-tight focus:outline-none focus:border-slate-900" />
        <button onClick={() => onSave(v)}
          data-testid={`${testid}-save`}
          className="bg-slate-900 text-white px-5 py-2.5 rounded-lg font-medium text-sm hover:bg-slate-800 transition-colors">
          Kaydet
        </button>
      </div>
      {hint && <p className="text-xs text-slate-500 mt-2">{hint}</p>}
    </div>
  );
}

// ============ TAB: RISKY MESSAGES ============
function RiskyMessagesTab() {
  const [convs, setConvs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [showAllMsgs, setShowAllMsgs] = useState(false);

  useEffect(() => {
    api.get("/admin/conversations", { params: { risky_only: true } }).then(({ data }) => setConvs(data));
    api.get("/admin/messages/risky").then(({ data }) => setMsgs(data));
  }, []);

  return (
    <div className="space-y-8" data-testid="admin-risky-messages">
      <div>
        <p className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-3">Riskli konuşmalar ({convs.length})</p>
        {convs.length === 0 ? <p className="text-slate-400 text-sm py-6">Riskli konuşma bulunamadı.</p> : (
          <div className="space-y-2">
            {convs.map((c) => (
              <div key={c.id} className="p-4 rounded-xl bg-white" data-testid={`admin-risky-conv-${c.id}`}>
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <Link to={`/mezat/${c.listing_id}`} className="font-medium text-slate-900 hover:underline">{c.listing_title}</Link>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {c.buyer_name} ↔ {c.seller_name} · Son mesaj: <span className="italic">&ldquo;{c.last_message_preview}&rdquo;</span>
                    </p>
                    <div className="flex gap-1.5 mt-1.5 flex-wrap">
                      {c.risk_flags?.map((f) => (
                        <span key={f} className="text-[10px] uppercase tracking-[0.2em] font-medium px-2 py-0.5 rounded bg-red-50 text-[#B54037]">{f}</span>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 font-mono-tight">{new Date(c.last_message_at).toLocaleString("tr-TR")}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-400">Riskli mesajlar ({msgs.length})</p>
          <button onClick={() => setShowAllMsgs((v) => !v)} className="text-xs font-medium text-slate-500 hover:text-slate-900" data-testid="risky-toggle-msgs">
            {showAllMsgs ? "Gizle" : "Tümünü göster"}
          </button>
        </div>
        {showAllMsgs && (
          <div className="space-y-2">
            {msgs.map((m) => (
              <div key={m.id} className="p-4 rounded-xl bg-white" data-testid={`admin-risky-msg-${m.id}`}>
                <p className="text-sm text-slate-800">{m.text}</p>
                <div className="flex items-center gap-2 mt-2 flex-wrap text-xs">
                  <span className="text-slate-500">Gönderen: {m.sender_name}</span>
                  <span className="text-slate-300">·</span>
                  <span className="font-mono-tight text-slate-400">{new Date(m.created_at).toLocaleString("tr-TR")}</span>
                  {m.risk_flags?.map((f) => (
                    <span key={f} className="text-[10px] uppercase tracking-[0.2em] font-medium px-2 py-0.5 rounded bg-red-50 text-[#B54037]">{f}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ TAB: AI USAGE ============
function AIUsageTab() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/ai-usage").then(({ data }) => setD(data)); }, []);
  if (!d) return <p className="text-slate-400">Yükleniyor…</p>;
  const cards = [
    { label: "Toplam AI çağrısı", value: d.total },
    { label: "Mock modda", value: d.mocked },
    { label: "Canlı LLM oranı", value: `%${(d.live_ratio * 100).toFixed(0)}` },
  ];
  return (
    <div className="space-y-8" data-testid="admin-ai-usage">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="p-6 rounded-xl bg-slate-50">
            <p className="text-[10px] uppercase tracking-[0.24em] font-medium text-slate-400 mb-3">{c.label}</p>
            <p className="font-mono-tight text-3xl font-medium text-slate-900 tracking-tight">{c.value}</p>
          </div>
        ))}
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-3">Son AI istekleri</p>
        {d.recent.length === 0 ? <p className="text-slate-400 text-sm py-6">Henüz AI çağrısı yok.</p> : (
          <div className="space-y-2">
            {d.recent.slice(0, 30).map((r) => (
              <div key={r.id} className="p-4 rounded-xl bg-white flex items-center gap-4 flex-wrap" data-testid={`ai-usage-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-800 truncate">{r.note_preview}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{r.user_name} · {new Date(r.created_at).toLocaleString("tr-TR")}</p>
                </div>
                <div className="flex gap-2 items-center">
                  {r.risk_level && (
                    <span className={`text-[10px] uppercase tracking-[0.2em] font-medium px-2 py-0.5 rounded ${
                      r.risk_level === "high" ? "bg-red-50 text-[#B54037]" :
                      r.risk_level === "medium" ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-800"
                    }`}>Risk: {r.risk_level} ({r.risk_score})</span>
                  )}
                  {r.mocked && <span className="text-[10px] uppercase tracking-[0.2em] font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-600">Mock</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ TAB: BLOG / REHBER ============
function BlogTab() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", excerpt: "", body: "", seo_title: "", seo_description: "" });

  const load = async () => {
    const { data } = await api.get("/admin/blog");
    setPosts(data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing?.id) {
        await api.patch(`/admin/blog/${editing.id}`, form);
        toast.success("Yazı güncellendi");
      } else {
        await api.post("/admin/blog", { ...form, status: "published" });
        toast.success("Yazı yayınlandı");
      }
      setEditing(null);
      setForm({ title: "", excerpt: "", body: "", seo_title: "", seo_description: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/admin/blog/${id}`);
    load();
  };

  const startEdit = (p) => {
    setEditing(p);
    setForm({ title: p.title, excerpt: p.excerpt, body: p.body,
              seo_title: p.seo_title || "", seo_description: p.seo_description || "" });
  };

  return (
    <div className="space-y-6" data-testid="admin-blog">
      <div className="p-5 rounded-xl bg-slate-50 space-y-3">
        <p className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-400">
          {editing ? "Yazıyı düzenle" : "Yeni rehber yazısı"}
        </p>
        <input placeholder="Başlık" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
          data-testid="blog-form-title"
          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900" />
        <input placeholder="Kısa özet" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
          data-testid="blog-form-excerpt"
          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900" />
        <textarea placeholder="İçerik (**kalın** desteklenir)" rows={5} value={form.body}
          onChange={(e) => setForm({ ...form, body: e.target.value })}
          data-testid="blog-form-body"
          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900" />
        <div className="grid grid-cols-2 gap-2">
          <input placeholder="SEO başlık (opsiyonel)" value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })}
            className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900" />
          <input placeholder="SEO açıklama (opsiyonel)" value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })}
            className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900" />
        </div>
        <div className="flex justify-end gap-2">
          {editing && <button onClick={() => { setEditing(null); setForm({ title: "", excerpt: "", body: "", seo_title: "", seo_description: "" }); }}
            className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">İptal</button>}
          <button onClick={save} data-testid="blog-form-save"
            className="bg-slate-900 text-white px-5 py-2.5 rounded-lg font-medium text-sm hover:bg-slate-800">
            {editing ? "Güncelle" : "Yayınla"}
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {posts.map((p) => (
          <div key={p.id} className="p-4 rounded-xl bg-white flex items-center gap-4 flex-wrap" data-testid={`admin-blog-${p.id}`}>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-slate-900">{p.title}</p>
              <p className="text-xs text-slate-500 truncate">{p.excerpt}</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-1">/{p.slug}</p>
            </div>
            <div className="flex gap-2">
              <Link to={`/rehber/${p.slug}`} target="_blank" className="text-xs font-medium text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded-lg hover:bg-slate-50">Görüntüle</Link>
              <button onClick={() => startEdit(p)} data-testid={`blog-edit-${p.id}`}
                className="text-xs font-medium text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded-lg hover:bg-slate-50">Düzenle</button>
              <button onClick={() => del(p.id)} data-testid={`blog-del-${p.id}`}
                className="text-xs font-medium text-[#B54037] hover:bg-red-50 px-3 py-1.5 rounded-lg">Sil</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

